import React from 'react';
import SEOHead from '../components/SEOHead';
import { Box, Container, Typography, Grid, Card, CardContent, Avatar, Button } from '@mui/material';
import { School, People, Star, Language } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import person from '../assets/images/person.webp';
import team1 from '../assets/images/team1.webp';
import team2 from '../assets/images/team2.webp';
import team3 from '../assets/images/team3.webp';
import team4 from '../assets/images/team4.webp';
import banner from '../assets/images/banner03.webp';

function AboutUs() {
  const navigate = useNavigate();

  const teamMembers = [
    {
      name: 'Chihui Cao',
      title: 'Founder & Chief Instructor',
      experience: '10+ years',
      specialization: 'International Chinese Education',
      avatar: person,
      description: 'Master\'s in International Chinese Teaching from NIE, NTU. Former Chinese teacher at University of Kentucky Confucius Institute, lecturer at Yangtze University, and Chinese Department Head at ASE Education Group.'
    },
    {
      name: 'William Zhang',
      title: 'Senior Chinese Instructor',
      experience: '8+ years',
      specialization: 'IB & IGCSE Chinese',
      avatar: team1,
      description: 'Specialized in IB and IGCSE Chinese curriculum development. Expert in helping students achieve excellent results in international examinations.'
    },
    {
      name: 'Tina Zheng',
      title: 'Business Chinese Specialist',
      experience: '6+ years',
      specialization: 'Business Communication',
      avatar: team2,
      description: 'Focuses on business Chinese and professional communication skills. Helps professionals enhance their workplace Chinese proficiency.'
    },
    {
      name: 'Joyce Chen',
      title: 'Youth Program Coordinator',
      experience: '7+ years',
      specialization: 'Children & Teen Education',
      avatar: team3,
      description: 'Specialized in teaching Chinese to children and teenagers. Creates engaging and interactive learning experiences for young learners.'
    },
    {
      name: 'Arlena',
      title: 'Cultural Immersion Program Director',
      experience: '6+ years',
      specialization: 'Cultural Immersion & Youth Education',
      avatar: team4,
        description: 'Specialized in designing immersive Chinese cultural experiences. Creates authentic learning environments that help students "live in Chinese" through cultural workshops and activities.'
    }
  ];

  const stats = [
    { icon: <School />, number: '500+', label: 'Students Taught' },
    { icon: <People />, number: '5', label: 'Expert Teachers' },
    { icon: <Star />, number: '98%', label: 'Success Rate' },
    { icon: <Language />, number: '10+', label: 'Years Experience' }
  ];

  return (
    <>
      <SEOHead pageKey="aboutus" />
      <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa'}}>
      {/* Header Section */}
      <Box sx={{ position: 'relative' }}>
        <Box
          component="img"
          src={banner}
          alt="About Us Banner"
          sx={{
            height: { xs: '200px', md: '500px' },
            width: '100%',
            objectFit: 'cover'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: { xs: 2, md: 4 }
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                color: 'white',
                textShadow: '0 4px 12px rgba(0,0,0,0.4)',
                fontWeight: 600,
                mb: 0.5,
                letterSpacing: 0.5,
                fontSize: { xs: '1.8rem', md: '3rem' }
              }}
            >
              智汇中文，沟通世界
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: 'rgba(255,255,255,0.95)',
                textShadow: '0 2px 8px rgba(0,0,0,0.35)',
                fontWeight: 500,
                letterSpacing: 0.2,
                fontSize: { xs: '0.95rem', md: '1.1rem' }
              }}
            >
              Connecting the world through Mandarin wisdom
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Company Introduction */}
      <Box sx={{ py: { xs: 4, md: 12 } }}>
        <Container maxWidth="xl">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h3"
                sx={{
                  fontFamily: 'serif',
                  fontWeight: 700,
                  color: '#07284b',
                  mb: { xs: 2, md: 4 },
                  fontSize: { xs: '2rem', md: '2.5rem' }
                }}
              >
                Our Mission
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#5a6c7d',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: { xs: 1, md: 3 }
                }}
              >
                At CHIHUI Mandarin Hub, our mission is to empower learners worldwide to communicate confidently in Mandarin. Guided by our principle — <b>“ 智汇中文，沟通世界  ”</b> — we design intelligent, systematic, and results-oriented learning solutions.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: '#5a6c7d',
                  fontSize: '1.1rem',
                  lineHeight: 1.8,
                  mb: 4
                }}
              >
                Our programs combine smart methodology, professional instruction, and real-world application, enabling learners to achieve fluency with efficiency and clarity. We are committed to transforming Mandarin learning into a strategic advantage for personal, academic, and professional growth.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  bgcolor: '#9f2122',
                  color: 'white',
                  px: 4,
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderRadius: 3,
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#8a1e1f',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 25px rgba(159, 33, 34, 0.4)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Start Your Journey
              </Button>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  p: 4,
                  bgcolor: 'white',
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <Grid container spacing={3}>
                  {stats.map((stat, index) => (
                    <Grid item xs={6} key={index}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            bgcolor: '#07284b',
                            color: 'white',
                            mb: 2
                          }}
                        >
                          {stat.icon}
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: '#07284b',
                            mb: 1
                          }}
                        >
                          {stat.number}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#5a6c7d',
                            fontWeight: 500
                          }}
                        >
                          {stat.label}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Founder Section */}
      <Box
        sx={{
          py: { xs: 6, md: 12 },
          background: 'linear-gradient(135deg, rgba(7, 40, 75, 0.03) 0%, rgba(159, 33, 34, 0.02) 100%)'
        }}
      >
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{
              fontFamily: 'serif',
              fontWeight: 700,
              color: '#07284b',
              mb: 6,
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Meet Our Founder
          </Typography>
          
          <Grid container spacing={ { xs: 2, md: 6 }} alignItems="center">
            <Grid item xs={12} md={4}>
              <Box sx={{ textAlign: 'center' }}>
                <Avatar
                  src={person}
                  sx={{
                    width: 200,
                    height: 200,
                    mx: 'auto',
                    mb: 3,
                    border: '4px solid white',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.15)'
                  }}
                />
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#07284b',
                    mb: 1
                  }}
                >
                  Ms. Chihui Cao
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    color: '#9f2122',
                    fontWeight: 600,
                    mb: 2
                  }}
                >
                  Founder & Chief Instructor
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={8}>
              <Card
                sx={{
                  p: 4,
                  bgcolor: 'white',
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)'
                }}
              >
                <CardContent>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      mb: 3
                    }}
                  >
                    Ms. Chihui Cao is the founder and chief instructor of CHIHUI Mandarin Hub, 
                    with over ten years of experience in international Chinese education and 
                    cross-cultural communication.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      mb: 3
                    }}
                  >
                    She graduated from the National Institute of Education, Nanyang Technological 
                    University, with a Master's degree in International Chinese Teaching, and holds 
                    a Master's degree in Radio and Television Arts from Shanghai University and a 
                    Bachelor's degree in Chinese Language and Literature.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      mb: 3
                    }}
                  >
                    Before founding CHIHUI Mandarin Hub, Ms. Cao taught at schools and renowned 
                    educational institutions in the United States, China, and Singapore. She served 
                    as a Chinese teacher at the University of Kentucky Confucius Institute, where 
                    she organized Chinese cultural workshops and speech competitions, promoting 
                    cultural exchange between local and international students.
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#5a6c7d',
                      fontSize: '1.1rem',
                      lineHeight: 1.8,
                      fontStyle: 'italic'
                    }}
                  >
                    "Learning Chinese is a journey of exploring culture and the world." 
                    - Ms. Chihui Cao
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box sx={{ py: { xs: 6, md: 12 } }}>
        <Container maxWidth="xl">
          <Typography
            variant="h3"
            sx={{
              fontFamily: 'serif',
              fontWeight: 700,
              color: '#07284b',
              mb: 2,
              textAlign: 'center',
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            Our Expert Team
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: '#5a6c7d',
              mb: 6,
              textAlign: 'center',
              maxWidth: 600,
              mx: 'auto'
            }}
          >
            A dedicated team of experienced Chinese language professionals committed to your success
          </Typography>
          
          <Grid container spacing={4}>
            {teamMembers.map((member, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card
                  sx={{
                    height: '100%',
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 16px 48px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Avatar
                      src={member.avatar}
                      sx={{
                        width: 100,
                        height: 100,
                        mx: 'auto',
                        mb: 3,
                        border: '3px solid white',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#07284b',
                        mb: 1
                      }}
                    >
                      {member.name}
                    </Typography>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        color: '#9f2122',
                        fontWeight: 600,
                        mb: 2
                      }}
                    >
                      {member.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#5a6c7d',
                        mb: 2,
                        fontWeight: 500
                      }}
                    >
                      {member.experience} • {member.specialization}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#5a6c7d',
                        fontSize: '0.9rem',
                        lineHeight: 1.6
                      }}
                    >
                      {member.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box
        sx={{
          py: 12,
          background: 'linear-gradient(135deg, #07284b 0%, #9f2122 100%)',
          color: 'white'
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontFamily: 'serif',
                fontWeight: 700,
                mb: 3,
                fontSize: { xs: '2rem', md: '2.5rem' }
              }}
            >
              Ready to Start Your Chinese Learning Journey?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto'
              }}
            >
              Join hundreds of successful students who have achieved their Chinese language goals with CHIHUI Mandarin
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={() => navigate('/')}
              sx={{
                bgcolor: 'white',
                color: '#07284b',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                borderRadius: 3,
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0,0,0,0.3)'
                },
                transition: 'all 0.3s ease'
              }}
            >
              Get Started Today
            </Button>
          </Box>
        </Container>
      </Box>
      </Box>
    </>
  );
}

export default AboutUs;
